import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Alert, Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import OrganisationService from '../../../shared/services/organisation/organisation.service'
import { ILedgerCoverageCorrectionPeriod } from '../../../shared/services/organisation/organisation.model'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'

interface LedgerCoverageCorrectionDialogProps {
  open: boolean;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000
// Releasing and committing a period's staged bills is a few minutes at worst. Past this
// the worker is not coming back, and polling forever would leave the user watching a
// spinner with no explanation.
const MAX_POLL_ATTEMPTS = 150

const formatKg = (value?: number | null): string => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '-'
  if (Math.abs(numeric) >= 1000) return `${(numeric / 1000).toFixed(2)} t CO2e`
  return `${Math.round(numeric * 100) / 100} kg CO2e`
}

const periodLabel = (period: ILedgerCoverageCorrectionPeriod): string => {
  if (period.name) return period.name
  const from = period.startDate ? period.startDate.slice(0, 10) : '?'
  const to = period.endDate ? period.endDate.slice(0, 10) : '?'
  return `${from} to ${to}`
}

/**
 * Offered when an organisation turns on bill import for its accounting syncs.
 *
 * While the setting was off, every sync fetched supplier bills but set them aside, so
 * coverage was visible without changing any figures. This asks whether those bills
 * should now be brought in, per report period, and states the impact in the
 * organisation's own numbers before anything changes. Bills that already carry a
 * category commit straight away; the rest land in the review queue; likely duplicates
 * of payments already imported are flagged for review, never counted twice. Locked
 * periods never appear.
 */
export const LedgerCoverageCorrectionDialog: React.FC<LedgerCoverageCorrectionDialogProps> = ({ open, onClose }) => {
  const [periods, setPeriods] = useState<ILedgerCoverageCorrectionPeriod[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const organisationService = new OrganisationService()
  const userNotificationsContext = useContext(UserNotificationsContext)

  // Held in a ref so the interval can always be cleared, including on unmount.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])
  useEffect(() => stopPolling, [stopPolling])

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    setErrorMessage('')
    organisationService.getLedgerCoverageCorrectionPreview()
      .then(response => {
        const found = response.periods || []
        setPeriods(found)
        setSelectedIds(found.map(p => p.reportPeriodId))
      })
      .catch(() => setErrorMessage('Could not work out what would change. Bill import is on for future syncs; you can import earlier bills later from this setting.'))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = (reportPeriodId: string) => {
    setSelectedIds(current => current.includes(reportPeriodId)
      ? current.filter(id => id !== reportPeriodId)
      : [...current, reportPeriodId])
  }

  const selected = periods.filter(p => selectedIds.includes(p.reportPeriodId))
  const totalKg = selected.reduce((sum, p) => sum + (Number(p.kgAddedToReports) || 0), 0)
  const totalNeedsReview = selected.reduce((sum, p) => sum + (Number(p.needsReviewRows) || 0), 0)
  const totalDuplicates = selected.reduce((sum, p) => sum + (Number(p.duplicateRows) || 0), 0)

  const handleClose = () => {
    stopPolling()
    setErrorMessage('')
    onClose()
  }

  const handleApply = async () => {
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const response = await organisationService.startLedgerCoverageCorrection(selectedIds)
      if (!response?.success) {
        setErrorMessage(response?.message || 'Could not start the bill import.')
        setIsSubmitting(false)
        return
      }

      const jobIds = (response.jobs || []).map(job => job.id)
      userNotificationsContext.openSnackbar({
        open: true,
        message: `Importing bills into ${jobIds.length} report period${jobIds.length === 1 ? '' : 's'}. This runs in the background.`,
        duration: 0,
        backgroundColor: 'primary.main',
      })
      onClose()

      let attempts = 0
      stopPolling()
      pollRef.current = setInterval(async () => {
        attempts += 1
        if (attempts > MAX_POLL_ATTEMPTS) {
          stopPolling()
          userNotificationsContext.openSnackbar({
            open: true,
            message: 'The bill import is taking longer than expected. It is still running; check the review queue shortly.',
            duration: 8000,
            backgroundColor: 'primary.main',
          })
          return
        }
        try {
          const { jobs } = await organisationService.getLedgerCoverageCorrectionJobs(jobIds)
          if (!jobs?.length || jobs.some(job => job.status === 'PENDING' || job.status === 'IN_PROGRESS')) return

          stopPolling()
          const failed = jobs.filter(job => job.status === 'FAILED')
          if (failed.length) {
            userNotificationsContext.openSnackbar({
              open: true,
              message: failed[0].errorMessage || 'The bill import failed for one or more report periods.',
              duration: 8000,
              backgroundColor: 'error.main',
            })
            return
          }
          const committed = jobs.reduce((sum, job) => sum + (job.committedRows || 0), 0)
          const needsReview = jobs.reduce((sum, job) => sum + (job.needsReviewRows || 0), 0)
          const duplicates = jobs.reduce((sum, job) => sum + (job.duplicateRows || 0), 0)
          userNotificationsContext.openSnackbar({
            open: true,
            message: `Bills imported. ${committed} committed`
              + (needsReview ? `, ${needsReview} waiting in your review queue` : '')
              + (duplicates ? `, ${duplicates} flagged as possible duplicates for you to check` : '')
              + '.',
            duration: 6000,
            backgroundColor: 'success.main',
          })
        } catch (error) {
          console.error(error)
        }
      }, POLL_INTERVAL_MS)
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || 'Could not start the bill import.')
      setIsSubmitting(false)
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title='Import the bills from your earlier syncs?'
      cancelHandler={handleClose}
      submitHandler={handleApply}
      isSubmitting={isSubmitting}
      disableSaveButton={isLoading || selected.length === 0}
      maxWidth='md'
      submitButtonText='Import bills for selected periods'
      submitButtonLoadingText='Starting'
    >
      {isLoading ? <LoadingSpinner /> : (
        <Box>
          <Typography variant='body2' sx={{ mb: 2 }}>
            From now on your accounting syncs will bring in supplier bills as well as bank
            transactions. Your earlier syncs also found bills and set them aside; nothing about
            your figures changes until you choose to import them here.
          </Typography>

          {periods.length === 0 ? (
            <Alert severity='info'>
              Nothing waiting to import. Bills found by future syncs will come straight into your
              review queue.
            </Alert>
          ) : (
            <>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox' />
                    <TableCell sx={{ fontWeight: 600 }}>Report period</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Bills found</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Commit now</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Need review</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 600 }}>Added to reports</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periods.map(period => (
                    <TableRow key={period.reportPeriodId} hover>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedIds.includes(period.reportPeriodId)}
                          onChange={() => toggle(period.reportPeriodId)}
                        />
                      </TableCell>
                      <TableCell>{periodLabel(period)}</TableCell>
                      <TableCell align='right'>{period.stagedRows}</TableCell>
                      <TableCell align='right'>{period.committableRows}</TableCell>
                      <TableCell align='right'>{period.needsReviewRows + period.duplicateRows}</TableCell>
                      <TableCell align='right'>{formatKg(period.kgAddedToReports)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Selected: {formatKg(totalKg)} will be added to your reported spend-based emissions
                  when the categorised bills commit.
                </Typography>
                {totalNeedsReview > 0 && (
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {totalNeedsReview} bill{totalNeedsReview === 1 ? '' : 's'} will wait in your review
                    queue for a category before counting.
                  </Typography>
                )}
                {totalDuplicates > 0 && (
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {totalDuplicates} bill{totalDuplicates === 1 ? ' looks' : 's look'} like a payment you
                    already imported. They will be flagged in the review queue for you to keep or discard,
                    never counted twice automatically.
                  </Typography>
                )}
                <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Locked report periods are not listed and are never changed.
                </Typography>
              </Box>
            </>
          )}

          {errorMessage && <Alert severity='error' sx={{ mt: 2 }}>{errorMessage}</Alert>}
        </Box>
      )}
    </DialogWrapper>
  )
}
